import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DollarSign, FileText, Package, Key, RefreshCw, Loader2, ArrowLeft,
  Search, CheckCircle, AlertTriangle, Clock, Building2, Shield,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

export default function NinjaFinance() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [importing, setImporting] = useState(null);

  // Data
  const [accounts, setAccounts] = useState({ items: [], total: 0 });
  const [agreements, setAgreements] = useState({ items: [], total: 0 });
  const [invoices, setInvoices] = useState({ items: [], total: 0 });
  const [products, setProducts] = useState({ items: [], total: 0 });
  const [licenses, setLicenses] = useState({ items: [], total: 0 });

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.get('/ninja-finance/health');
      setHealth(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const loadTab = useCallback(async (t) => {
    try {
      if (t === 'accounts') { const r = await api.get('/ninja-finance/billing/accounts'); setAccounts(r.data); }
      else if (t === 'agreements') { const r = await api.get('/ninja-finance/billing/agreements'); setAgreements(r.data); }
      else if (t === 'invoices') { const r = await api.get('/ninja-finance/billing/invoices'); setInvoices(r.data); }
      else if (t === 'products') { const r = await api.get('/ninja-finance/billing/products'); setProducts(r.data); }
      else if (t === 'licenses') { const r = await api.get('/ninja-finance/licenses'); setLicenses(r.data); }
    } catch { /* data may not exist yet */ }
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { if (tab !== 'overview') loadTab(tab); }, [tab, loadTab]);

  const handleImport = async (type) => {
    setImporting(type);
    try {
      if (type === 'billing') {
        const r = await api.post('/ninja-finance/billing/import');
        toast.success(`Billing imported: ${r.data.accounts?.created || 0} accounts, ${r.data.agreements?.created || 0} agreements, ${r.data.invoices?.created || 0} invoices, ${r.data.products?.created || 0} products`);
      } else {
        const r = await api.post('/ninja-finance/licenses/import');
        toast.success(`Licenses imported: ${r.data.licenses?.created || 0} licenses, ${r.data.assignments?.created || 0} assignments`);
      }
      loadHealth();
      loadTab(tab);
    } catch (e) {
      toast.error(`Import failed: ${e.response?.data?.detail || e.message}`);
    }
    setImporting(null);
  };

  const h = health || {};

  return (
    <div className="space-y-6 p-6" data-testid="ninja-finance-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ninjarmm')}><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="h-6 w-6 text-blue-600" /> NinjaOne Finance & Licenses</h1>
            <p className="text-sm text-gray-500">Provider billing and license reference data — read-only</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => handleImport('billing')} disabled={!!importing} data-testid="import-billing-btn">
            {importing === 'billing' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Import Billing
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleImport('licenses')} disabled={!!importing} data-testid="import-licenses-btn">
            {importing === 'licenses' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Key className="h-4 w-4 mr-1.5" />}
            Import Licenses
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="finance-overview">
        {[
          { label: 'Accounts', value: h.accounts || 0, icon: Building2 },
          { label: 'Agreements', value: h.agreements || 0, icon: FileText },
          { label: 'Invoices', value: h.invoices || 0, icon: DollarSign },
          { label: 'Products', value: h.products || 0, icon: Package },
          { label: 'Licenses', value: h.licenses || 0, icon: Key },
          { label: 'Assignments', value: h.assignments || 0, icon: CheckCircle },
          { label: 'Expired', value: h.expired_licenses || 0, icon: AlertTriangle, color: h.expired_licenses > 0 ? 'text-red-600' : undefined },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-3">
            <p className="text-[11px] font-medium text-gray-500 uppercase">{s.label}</p>
            <p className={`text-xl font-bold ${s.color || ''}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="agreements">Agreements</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="licenses">Licenses</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardContent className="p-6 text-center text-gray-500 space-y-3">
              <DollarSign className="h-12 w-12 mx-auto text-gray-300" />
              <p className="font-medium">NinjaOne Finance Overview</p>
              <p className="text-sm">Import billing data and licenses from NinjaOne to view here.</p>
              <p className="text-xs text-amber-600">
                <Shield className="h-3 w-3 inline mr-1" />
                NinjaOne billing records are provider references only — they do not create CipherOne receivables.
              </p>
              <div className="flex justify-center gap-3 text-xs text-gray-400">
                <span>Billing: {h.billing_last_sync ? formatInCST(h.billing_last_sync, 'MMM d, h:mm a') : 'Never synced'}</span>
                <span>Licenses: {h.license_last_sync ? formatInCST(h.license_last_sync, 'MMM d, h:mm a') : 'Never synced'}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts">
          <SimpleTable items={accounts.items} total={accounts.total} columns={[
            { key: 'name', label: 'Name' },
            { key: 'provider_status', label: 'Status' },
            { key: 'cipherone_customer_name', label: 'Customer', fallback: 'Unmapped' },
            { key: 'last_synced_at', label: 'Last Sync', format: 'time' },
          ]} emptyText="No billing accounts imported yet" />
        </TabsContent>

        <TabsContent value="agreements">
          <SimpleTable items={agreements.items} total={agreements.total} columns={[
            { key: 'name', label: 'Agreement' },
            { key: 'billing_frequency', label: 'Frequency' },
            { key: 'provider_status', label: 'Status' },
            { key: 'cipherone_customer_name', label: 'Customer', fallback: 'Unmapped' },
            { key: 'start_date', label: 'Start', format: 'date' },
            { key: 'end_date', label: 'End', format: 'date' },
          ]} emptyText="No agreements imported yet" />
        </TabsContent>

        <TabsContent value="invoices">
          <Card>
            <CardContent className="p-0">
              {invoices.items.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">No NinjaOne invoices imported yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {invoices.items.map(inv => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-sm">{inv.invoice_number || inv.provider_id}</TableCell>
                        <TableCell className="text-sm">{inv.cipherone_customer_name || <span className="text-gray-400">Unmapped</span>}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{inv.provider_status || '—'}</Badge></TableCell>
                        <TableCell className="font-medium">{inv.amount != null ? `$${Number(inv.amount).toFixed(2)}` : '—'}</TableCell>
                        <TableCell className="text-xs text-gray-500">{inv.billing_period_start || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50" data-testid="ninja-source-badge">NinjaOne</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products">
          <SimpleTable items={products.items} total={products.total} columns={[
            { key: 'name', label: 'Product' },
            { key: 'description', label: 'Description' },
            { key: 'quantity_type', label: 'Type' },
            { key: 'price', label: 'Price', format: 'money' },
            { key: 'billing_method', label: 'Billing' },
          ]} emptyText="No products imported yet" />
        </TabsContent>

        <TabsContent value="licenses">
          <Card>
            <CardContent className="p-0">
              {licenses.items.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">No software licenses imported yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>License</TableHead>
                    <TableHead>Publisher</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Purchased</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead>Available</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {licenses.items.map(lic => (
                      <TableRow key={lic.id} className="cursor-pointer hover:bg-gray-50">
                        <TableCell className="font-medium">{lic.name}</TableCell>
                        <TableCell className="text-sm text-gray-600">{lic.publisher || '—'}</TableCell>
                        <TableCell className="text-sm">{lic.cipherone_customer_name || <span className="text-gray-400">Unmapped</span>}</TableCell>
                        <TableCell className="text-center">{lic.purchased_quantity ?? '—'}</TableCell>
                        <TableCell className="text-center">{lic.assigned_quantity ?? '—'}</TableCell>
                        <TableCell className="text-center font-medium text-emerald-600">{lic.available_quantity ?? '—'}</TableCell>
                        <TableCell className="text-xs text-gray-500">{lic.expiration_date || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${
                            lic.provider_status === 'active' ? 'border-emerald-300 text-emerald-700' : 'border-gray-300'
                          }`}>{lic.provider_status || '—'}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleTable({ items, total, columns, emptyText }) {
  if (!items || items.length === 0) {
    return <Card><CardContent className="py-8 text-center text-gray-500 text-sm">{emptyText}</CardContent></Card>;
  }
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow>{columns.map(c => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={item.id || i}>
              {columns.map(c => (
                <TableCell key={c.key} className="text-sm">
                  {c.format === 'money' && item[c.key] != null ? `$${Number(item[c.key]).toFixed(2)}` :
                   c.format === 'time' && item[c.key] ? formatInCST(item[c.key], 'MMM d, h:mm a') :
                   c.format === 'date' && item[c.key] ? item[c.key] :
                   item[c.key] || <span className="text-gray-400">{c.fallback || '—'}</span>}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-gray-400 p-2 text-right">{total} total</p>
    </CardContent></Card>
  );
}
